import type { Request, Single, DeliverableEntry } from '@/lib/dd-queue/types'
import type { SubjectMutation } from '@/lib/dd-queue/types/subjectMutation'
import { ddQueueCurrentOperator } from '@/lib/dd-queue/ddQueueIdentity'
import {
  getActivePhase,
  hasOpenDeliverableForRole,
  hasOpenDeliverableForRoleAndUser,
  hasOpenDeliverableForUser,
} from '@/lib/dd-queue/workflow'
import { resolveSubject } from '@/lib/dd-queue/resolveSubject'
import { lineHasPdWork } from '@/lib/dd-queue/helpers'

export interface FilterState {
  path: string
  customer: string
  status: string
  age: string
  vip: string
  assignee: string
}

export const UNASSIGNED = '__unassigned__'

export const EMPTY_FILTERS: FilterState = {
  path: '',
  customer: '',
  status: '',
  age: '',
  vip: '',
  assignee: '',
}

export interface QueueState {
  role: string
  scope: string
  search: string
  filters: FilterState
}

function skusOf(req: Request): string[] {
  return req.isMaster ? req.children.map((c) => c.sku) : [(req as Single).sku]
}

function resolvedDeliverables(
  req: Request,
  sku: string,
  mutations: Record<string, SubjectMutation>
): Record<string, DeliverableEntry> {
  return resolveSubject(req, sku, mutations)?.deliverables ?? {}
}

function getMaxAge(req: Request): number {
  if (req.isMaster) return Math.max(...req.children.map((c) => c.age))
  return req.age
}

export function filterReqs(
  state: QueueState,
  source: Request[] = [],
  mutations: Record<string, SubjectMutation> = {}
): Request[] {
  let reqs = source.filter((r) => {
    const skus = skusOf(r)
    const resolved = skus.map((sku) => resolveSubject(r, sku, mutations))

    const hasWork = resolved.some((s) => s && lineHasPdWork(s))
    if (!hasWork && r.path !== 'chg') return false

    const allDone = resolved.every(
      (s) => !s || s.deletedAt != null || getActivePhase(s.deliverables) === 'complete'
    )
    if (allDone) return false

    if (state.filters.path && r.path !== state.filters.path) return false
    if (state.filters.customer && r.customer !== state.filters.customer) return false
    if (state.filters.vip === 'vip' && !r.vip) return false
    if (state.filters.vip === 'standard' && r.vip) return false

    if (state.search.trim()) {
      const q = state.search.toLowerCase()
      const inSingle = (s: Single) =>
        (s.product ?? '').toLowerCase().includes(q) || s.sku.toLowerCase().includes(q)
      const hit =
        r.id.toLowerCase().includes(q) ||
        (r.product ?? '').toLowerCase().includes(q) ||
        (r.customer ?? '').toLowerCase().includes(q) ||
        (r.submitter?.name ?? '').toLowerCase().includes(q) ||
        (r.isMaster
          ? r.children.some(
              (c) =>
                (c.name ?? c.sku).toLowerCase().includes(q) || c.sku.toLowerCase().includes(q)
            )
          : inSingle(r))
      if (!hit) return false
    }

    return true
  })

  if (state.role !== 'all' && state.scope === 'mine') {
    reqs = reqs.filter((r) =>
      skusOf(r).some((sku) =>
        hasOpenDeliverableForRoleAndUser(
          resolvedDeliverables(r, sku, mutations),
          state.role,
          ddQueueCurrentOperator().name
        )
      )
    )
  } else {
    if (state.role !== 'all') {
      reqs = reqs.filter((r) =>
        skusOf(r).some((sku) =>
          hasOpenDeliverableForRole(resolvedDeliverables(r, sku, mutations), state.role)
        )
      )
    }
    if (state.scope === 'mine') {
      reqs = reqs.filter((r) =>
        skusOf(r).some((sku) =>
          hasOpenDeliverableForUser(
            resolvedDeliverables(r, sku, mutations),
            ddQueueCurrentOperator().name
          )
        )
      )
    }
  }

  if (state.filters.status) {
    const stepKey = state.filters.status
    reqs = reqs.filter((r) =>
      skusOf(r).some(
        (sku) => resolvedDeliverables(r, sku, mutations)[stepKey]?.status === 'open'
      )
    )
  }

  if (state.filters.assignee) {
    const want = state.filters.assignee
    reqs = reqs.filter((r) =>
      skusOf(r).some((sku) => {
        const resolved = resolveSubject(r, sku, mutations)
        if (want === UNASSIGNED && resolved && !lineHasPdWork(resolved)) return false
        const open = Object.values(resolvedDeliverables(r, sku, mutations)).filter(
          (e) => e.status === 'open'
        )
        return want === UNASSIGNED
          ? open.some((e) => !e.assignee)
          : open.some((e) => e.assignee === want)
      })
    )
  }

  if (state.filters.age) {
    reqs = reqs.filter((r) => {
      const age = getMaxAge(r)
      if (state.filters.age === 'lt1') return age < 1
      if (state.filters.age === '1to3') return age >= 1 && age < 3
      if (state.filters.age === '3to7') return age >= 3 && age < 7
      if (state.filters.age === 'gt7') return age >= 7
      return true
    })
  }

  return reqs
}
